/** Employee directory and invitations. */
import { DataTable } from "@primereact/ui/datatable";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  SUPPORTED_LOCALES,
  type Locale,
  type RoleResponse,
} from "@offertli/shared";
import { StatusBadge } from "../../components/StatusBadge.js";
import { Button } from "../../components/ui/Button.js";
import { Checkbox } from "@primereact/ui/checkbox";
import { Select as Dropdown } from "@primereact/ui/select";
import { Field } from "../../components/ui/Field.js";
import { InputText as Input } from "@primereact/ui/inputtext";
import { PageHeader } from "../../components/ui/PageHeader.js";
import { apiGet, apiPost } from "../../lib/api.js";
import { useMe, usePermissions } from "../../lib/auth.js";
import { ErrorPanel, LoadingPanel, MutationError, Section } from "../shared.js";
import type { EmployeeListRow } from "./types.js";

export function EmployeesPage() {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const me = useMe();
  const [search, setSearch] = useState("");
  const [showInvite, setShowInvite] = useState(false);
  const canList = permissions.has("employees.view_all");
  const employees = useQuery({
    queryKey: ["employees", search],
    queryFn: () =>
      apiGet<{
        data: EmployeeListRow[];
      }>("/employees", {
        query: {
          search,
          status: "all",
        },
      }).then((response) => response.data),
    enabled: canList,
  });
  if (!canList) {
    if (me.isLoading) return <LoadingPanel />;
    if (me.data?.user.id) {
      return <Navigate to="/employees/me" replace />;
    }
    return <ErrorPanel error={me.error} />;
  }
  return (
    <>
      <PageHeader
        title={t("employees.title")}
        subtitle={t("employees.subtitle")}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {permissions.has("users.create") ? (
              <Button
                type="button"
                onClick={() => setShowInvite((value) => !value)}
              >
                {t("users.invite")}
              </Button>
            ) : null}
          </div>
        }
      />
      {showInvite ? (
        <InviteEmployeePanel onClose={() => setShowInvite(false)} />
      ) : null}
      <div className="mb-5 max-w-md">
        <Input
          aria-label={t("common.search")}
          placeholder={t("employees.search")}
          value={search}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setSearch(event.target.value)
          }
        />
      </div>
      {employees.isLoading ? (
        <LoadingPanel />
      ) : employees.error ? (
        <ErrorPanel error={employees.error} />
      ) : (
        <DataTable.Root>
          <DataTable.TableContainer>
            <DataTable.Table>
              <DataTable.THead>
                <DataTable.Row>
                  <DataTable.THeadCell>
                    <DataTable.THeadTitle>
                      {t("users.name")}
                    </DataTable.THeadTitle>
                  </DataTable.THeadCell>
                  <DataTable.THeadCell>
                    <DataTable.THeadTitle>
                      {t("employees.jobTitle")}
                    </DataTable.THeadTitle>
                  </DataTable.THeadCell>
                  <DataTable.THeadCell>
                    <DataTable.THeadTitle>
                      {t("employees.pensum")}
                    </DataTable.THeadTitle>
                  </DataTable.THeadCell>
                  <DataTable.THeadCell>
                    <DataTable.THeadTitle>
                      {t("common.status")}
                    </DataTable.THeadTitle>
                  </DataTable.THeadCell>
                  <DataTable.THeadCell>
                    <DataTable.THeadTitle>
                      {t("common.email")}
                    </DataTable.THeadTitle>
                  </DataTable.THeadCell>
                </DataTable.Row>
              </DataTable.THead>
              <DataTable.TBody className="divide-y divide-app-border">
                {employees.data?.map((employee) => (
                  <DataTable.Row key={employee.id}>
                    <DataTable.Cell className="font-medium">
                      <Link
                        className="text-brand hover:underline"
                        to={`/employees/${employee.id}`}
                      >
                        {employee.firstName} {employee.lastName}
                      </Link>
                      <span className="ml-2 text-xs text-app-muted">
                        {employee.employeeNumber}
                      </span>
                    </DataTable.Cell>
                    <DataTable.Cell>{employee.jobTitle || "—"}</DataTable.Cell>
                    <DataTable.Cell>{employee.pensumPercent}%</DataTable.Cell>
                    <DataTable.Cell>
                      <StatusBadge status={employee.status} />
                    </DataTable.Cell>
                    <DataTable.Cell>{employee.email}</DataTable.Cell>
                  </DataTable.Row>
                ))}
                {!employees.data?.length ? (
                  <DataTable.Row>
                    <DataTable.Cell
                      colSpan={5}
                      className="py-10 text-center text-app-muted"
                    >
                      {t("employees.empty")}
                    </DataTable.Cell>
                  </DataTable.Row>
                ) : null}
              </DataTable.TBody>
            </DataTable.Table>
          </DataTable.TableContainer>
        </DataTable.Root>
      )}
    </>
  );
}
function InviteEmployeePanel({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [locale, setLocale] = useState<Locale>("en");
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const roles = useQuery({
    queryKey: ["roles"],
    queryFn: () =>
      apiGet<{ data: RoleResponse[] }>("/roles").then(
        (response) => response.data,
      ),
  });
  const invite = useMutation({
    mutationFn: () =>
      apiPost("/users", { firstName, lastName, email, locale, roleIds }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["employees"] });
      onClose();
    },
  });
  return (
    <Section title={t("users.newInvitation")} className="mb-5">
      <form
        className="grid gap-4 sm:grid-cols-2"
        onSubmit={(event) => {
          event.preventDefault();
          invite.mutate();
        }}
      >
        <Field label={t("common.firstName")} required>
          <Input
            required
            value={firstName}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setFirstName(event.target.value)
            }
          />
        </Field>
        <Field label={t("common.lastName")} required>
          <Input
            required
            value={lastName}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setLastName(event.target.value)
            }
          />
        </Field>
        <Field label={t("common.email")} required>
          <Input
            required
            type="email"
            value={email}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setEmail(event.target.value)
            }
          />
        </Field>
        <Field label={t("common.language")}>
          <Dropdown.Root
            value={locale}
            onValueChange={(event: { value: unknown }) =>
              setLocale(event.value as Locale)
            }
            fluid
            options={SUPPORTED_LOCALES.map((value) => ({
              value,
              label: t(`locale.${value}`),
            }))}
            optionLabel="label"
            optionValue="value"
          >
            <Dropdown.Trigger type="button">
              <Dropdown.Value />
              <Dropdown.Indicator className="pi pi-chevron-down" />
            </Dropdown.Trigger>
            <Dropdown.Portal>
              <Dropdown.Positioner>
                <Dropdown.Popup>
                  <Dropdown.List />
                </Dropdown.Popup>
              </Dropdown.Positioner>
            </Dropdown.Portal>
          </Dropdown.Root>
        </Field>
        <fieldset className="sm:col-span-2">
          <legend className="mb-2 text-sm font-medium">
            {t("users.roles")}
          </legend>
          <div className="flex flex-wrap gap-2">
            {roles.data?.map((role) => (
              <label
                key={role.id}
                className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm"
              >
                <Checkbox.Root
                  checked={roleIds.includes(role.id)}
                  onCheckedChange={(event: { checked: boolean }) =>
                    setRoleIds(
                      event.checked
                        ? [...roleIds, role.id]
                        : roleIds.filter((id) => id !== role.id),
                    )
                  }
                >
                  <Checkbox.Box>
                    <Checkbox.Indicator match="checked">
                      <i className="pi pi-check" />
                    </Checkbox.Indicator>
                  </Checkbox.Box>
                </Checkbox.Root>
                {role.name}
              </label>
            ))}
          </div>
        </fieldset>
        <MutationError error={invite.error} />
        <div className="flex gap-2 sm:col-span-2">
          <Button
            type="submit"
            loading={invite.isPending || undefined}
            disabled={!roleIds.length}
          >
            {t("users.sendInvite")}
          </Button>
          <Button
            type="button"
            variant="outlined"
            severity="secondary"
            onClick={onClose}
          >
            {t("common.cancel")}
          </Button>
        </div>
      </form>
    </Section>
  );
}
